import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, Send, CalendarCheck } from "lucide-react";
import BookingDialog from "@/components/site/BookingDialog";
import { API, http, waLink, expireAuth } from "@/lib/api";
import { useWaNumber } from "@/components/site/WhatsAppFab";
import { translateError } from "@/i18n/errors";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteUi } from "@/context/SiteUiContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const sessionId = (userId) => {
  const key = userId ? `ptg_chat_sid_${userId}` : "ptg_chat_sid";
  let id = localStorage.getItem(key);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(key, id); }
  return id;
};

function Rich({ text }) {
  return text.split(/(\*\*[^*]+\*\*|\/(?:tours|cars|guides|destinations|open-trip|planner|map)(?:\/[a-z0-9-]+)?)/g).map((part, i) => {
    if (part.startsWith("**")) return <b key={i}>{part.slice(2, -2)}</b>;
    if (/^\/(tours|cars|guides|destinations|open-trip|planner|map)/.test(part)) return <Link key={i} to={part} className="font-semibold text-[#E11D48] underline underline-offset-2">{part}</Link>;
    return part;
  });
}

async function streamChat(body, onDelta, onItems) {
  const token = localStorage.getItem("ptg_token");
  const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) })
    .catch(() => { throw new Error(translateError("network")); });
  if (!res.ok) {
    if (res.status === 401) expireAuth(token);
    const j = await res.json().catch(() => ({}));
    throw Object.assign(new Error(translateError(typeof j.detail === "string" ? j.detail : "invalid")), { status: res.status });
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let got = false;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const data = p.replace(/^data: /, "");
      if (data === "[DONE]") {
        if (!got) throw new Error(translateError("Assistant is unavailable, please try again"));
        return;
      }
      let j;
      try { j = JSON.parse(data); } catch { continue; }
      if (j.error) throw new Error(translateError(j.error));
      if (j.items) { onItems(j.items); continue; }
      got = true;
      onDelta(j.delta);
    }
  }
}

function BookButtons({ items, onBook }) {
  const { t, tr, money } = useLang();
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">{items.map((it) => {
      const name = it.type === "tour" ? tr(it, "name") : it.name;
      return (
        <button key={it.slug} data-testid={`chat-book-${it.slug}`} onClick={() => onBook({ type: it.type, slug: it.slug, name, price: it.price, days: it.days, city: it.city_slug })}
          className="chat-book-action inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-2xl bg-[#E11D48] px-3 py-2 text-left text-xs font-bold text-white [overflow-wrap:anywhere] transition-transform hover:-translate-y-0.5">
          <CalendarCheck className="h-3.5 w-3.5" />{t("chat_book")} · {name} · {money(it.price, it.city_slug, it.type === "tour" ? 1 : 0)}
        </button>
      );
    })}</div>
  );
}

function Bubble({ m, onBook }) {
  const mine = m.role === "user";
  const num = useWaNumber();
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
      <p data-testid={`chat-msg-${m.role}`} className={`max-w-[90%] whitespace-pre-wrap [overflow-wrap:anywhere] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${mine ? "rounded-br-md bg-[#064E3B] text-white" : m.error ? "rounded-bl-md bg-rose-50 text-rose-700" : "rounded-bl-md bg-slate-100 text-slate-800"}`}>
        {mine ? m.text : <Rich text={m.text} />}
        {m.error && num && <a href={waLink(num, m.question || "")} target="_blank" rel="noreferrer" data-testid="chat-error-wa-link" className="mt-1.5 block font-bold text-[#128C7E] underline">WhatsApp +{num}</a>}
      </p>
      {!mine && m.items?.length > 0 && <BookButtons items={m.items} onBook={onBook} />}
    </div>
  );
}

const today = () => new Date().toLocaleDateString("en-CA");

function useNudge(open) {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!pathname.startsWith("/tours")) { setShow(false); return undefined; }
    if (open) { setShow(false); return undefined; }
    if (localStorage.getItem("ptg_greet_day") === today()) return undefined;
    const id = setTimeout(() => { localStorage.setItem("ptg_greet_day", today()); setShow(true); }, 20000);
    return () => clearTimeout(id);
  }, [pathname, open]);
  return { show, dismiss: () => setShow(false) };
}

function Nudge({ onOpen, onDismiss }) {
  const { t } = useLang();
  return (
    <div data-testid="chat-proactive" className="fixed bottom-24 left-5 z-50 w-[min(300px,calc(100vw-2.5rem))] rounded-3xl rounded-bl-md border border-emerald-100 bg-white p-4 shadow-2xl animate-in fade-in slide-in-from-bottom-3 duration-500">
      <button onClick={onDismiss} data-testid="chat-proactive-dismiss" aria-label={t("chat_dismiss")} className="absolute right-2.5 top-2.5 flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
      <p className="pr-12 text-sm font-medium leading-relaxed text-slate-800">{t("chat_proactive")}</p>
      <div className="mt-3 flex gap-2">
        <button onClick={onOpen} data-testid="chat-proactive-open" className="min-h-11 rounded-full bg-[#064E3B] px-4 py-2 text-xs font-bold text-white transition-transform hover:-translate-y-0.5">{t("chat_proactive_open")}</button>
        <button onClick={onDismiss} data-testid="chat-proactive-later" className="min-h-11 rounded-full px-3 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-100">{t("chat_dismiss")}</button>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const { t, lang, currency } = useLang();
  const { user } = useAuth();
  const sid = useMemo(() => sessionId(user?.id), [user?.id]);
  const activeSid = useRef(sid);
  activeSid.current = sid;
  const { chatOpen: open, setChatOpen: setOpen, modalCount, mobile, editing } = useSiteUi();
  const [msgs, setMsgs] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(null);
  const nudge = useNudge(open || modalCount > 0 || editing);
  const messagesRef = useRef(null);
  const pinned = useRef(true);
  const [newMessages, setNewMessages] = useState(false);
  const latest = () => { pinned.current = true; setNewMessages(false); const el = messagesRef.current; if (el) el.scrollTop = el.scrollHeight; };
  const book = (item) => { setOpen(false); setBooking(item); };
  const openChat = () => { nudge.dismiss(); setOpen(true); };

  useEffect(() => { setMsgs(null); setBusy(false); pinned.current = true; setNewMessages(false); }, [sid]);
  useEffect(() => {
    let cancelled = false;
    if (open && msgs === null) http.get(`/chat/${sid}`).then((r) => { if (!cancelled) setMsgs(r.data); }).catch(() => { if (!cancelled) setMsgs([]); });
    return () => { cancelled = true; };
  }, [open, msgs, sid]);
  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (pinned.current) el.scrollTop = el.scrollHeight;
    else setNewMessages(true);
  }, [msgs, open]);

  const send = async (text) => {
    const q = text.trim();
    if (!q || busy) return;
    pinned.current = true;
    setNewMessages(false);
    setInput("");
    setBusy(true);
    setMsgs((m) => [...(m || []), { role: "user", text: q }, { role: "assistant", text: "" }]);
    const patch = (fn) => { if (activeSid.current === sid) setMsgs((m) => [...m.slice(0, -1), fn(m[m.length - 1])]); };
    try {
      await streamChat({ session_id: sid, message: q, lang, currency }, (d) => patch((last) => ({ ...last, text: last.text + d })), (items) => patch((last) => ({ ...last, items })));
    } catch (e) {
      if (e.status === 401) setInput((draft) => draft || q);
      patch(() => ({ role: "assistant", text: e.message, error: true, question: q }));
    }
    if (activeSid.current === sid) setBusy(false);
  };

  return (
    <>
      {booking && <BookingDialog key={booking.slug} item={booking} onClose={() => setBooking(null)} />}
      {!open && modalCount === 0 && !editing && nudge.show && <Nudge onOpen={openChat} onDismiss={nudge.dismiss} />}
      {!open && !mobile && modalCount === 0 && (
        <button onClick={openChat} data-testid="chat-open-btn" aria-label={t("chat_open")}
          className="fixed bottom-5 left-5 z-50 flex items-center gap-2 rounded-full bg-[#064E3B] py-3 pl-3 pr-3 sm:pr-5 text-sm font-bold text-white shadow-xl shadow-emerald-900/25 transition-transform hover:scale-105">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F59E0B] text-[#064E3B]"><Sparkles className="h-4 w-4" /></span>
          <span className="hidden sm:inline">{t("chat_open")}</span>
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen} modal={mobile}>
        <DialogContent data-testid="chat-panel" className="ptg-chat-panel" showClose={false} aria-describedby={undefined} onOpenAutoFocus={(e) => { if (mobile) e.preventDefault(); }}>
          <div className="chat-header flex shrink-0 items-center gap-3 bg-[#064E3B] px-4 py-3 text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F59E0B] text-[#064E3B]"><Sparkles className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1"><DialogTitle className="text-sm font-bold leading-snug">{t("chat_title")}</DialogTitle><p className="text-xs text-emerald-100">{t("chat_sub")}</p></div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" aria-label={t("chat_close")} className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full p-2 transition-colors hover:bg-white/15"><X className="h-5 w-5" /></button>
          </div>
          <div className="chat-language-note shrink-0 border-b border-slate-100 bg-emerald-50 px-4 py-2">
            <p data-testid="chat-language-hint" className="text-xs font-medium text-[#064E3B]">{t("chat_languages")}</p>
            <p data-testid="chat-account-state" className="mt-0.5 text-[11px] text-slate-500">{t(user ? "chat_identity" : "chat_guest")}</p>
          </div>
          <div ref={messagesRef} data-testid="chat-messages" onScroll={(e) => { const el = e.currentTarget; pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 64; if (pinned.current) setNewMessages(false); }} className="min-h-0 flex-1 space-y-2.5 overflow-y-auto overscroll-contain px-4 py-4 [overflow-anchor:none]">
            <Bubble m={{ role: "assistant", text: t("chat_hello") }} />
            {(msgs || []).map((m, i) => (m.text || !busy ? <Bubble key={i} m={m} onBook={book} /> : <p key={i} data-testid="chat-typing" className="text-xs italic text-slate-400">{t("chat_typing")}</p>))}
            {msgs?.length === 0 && (
              <div className="flex flex-wrap gap-2 pt-1">{["chat_q1", "chat_q2", "chat_q3"].map((k) => (
                <button key={k} onClick={() => send(t(k))} data-testid={`chat-quick-${k}`} className="min-h-11 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 transition-colors hover:bg-emerald-100">{t(k)}</button>
              ))}</div>
            )}
          </div>
          {newMessages && <button data-testid="chat-latest-messages" onClick={latest} className="min-h-11 shrink-0 border-t border-emerald-100 bg-emerald-50 px-4 py-2 text-sm font-bold text-[#064E3B]">{t("mobile_latest")}</button>}
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="chat-composer flex shrink-0 items-center gap-2 border-t border-slate-100 p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={500} placeholder={t("chat_ph")} data-testid="chat-input"
              className="min-h-11 min-w-0 flex-1 rounded-full border border-slate-200 px-4 text-base outline-none transition-colors focus:border-emerald-500" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-btn" aria-label={t("chat_send")}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E11D48] text-white transition-transform hover:scale-105 disabled:opacity-40"><Send className="h-4 w-4" /></button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
